<template>
  <div>
    <DynamicModal
      :showModal="showModalProp"
      :persistent="false"
      @clickOutside="closeModal"
      title="Overtime Entries"
      maxWidth="900px"
    >
      <v-container fluid slot="title-buttons" class="py-1">
        <v-row justify="end">
          <v-btn
            icon
            depressed
            color="primary"
            @click="closeModal"
          >
            <v-icon color="white">close</v-icon>
          </v-btn>
        </v-row>
      </v-container>
      <v-container fluid slot="content">
        <v-row class="mt-2">
          <v-col cols="12">
            <SimplePageHeader title="Assignment"></SimplePageHeader>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              label="Consultant"
              v-bind:value="consultantName"
              readonly
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              label="Client"
              v-bind:value="clientName"
              readonly
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="6" md="3">
            <FormattedNumberField
              label="Hourly Pay Rate"
              :value="hourlyPayRate"
              :readonly="true"
              prepend-inner-icon="attach_money"
            ></FormattedNumberField>
          </v-col>
          <v-col cols="6" md="3">
            <FormattedNumberField
              label="Hourly Bill Rate"
              :value="hourlyBillRate"
              :readonly="true"
              prepend-inner-icon="attach_money"
            ></FormattedNumberField>
          </v-col>
        </v-row>
        <v-row
          v-if="showDateRangePicker"
          class="my-0"
        >
          <v-col cols="12">
            <DateRange
              class="my-0"
              :showLabel="true"
              :dateBegin="filterDateBeginValue"
              :dateEnd="filterDateEndValue"
              @dateBeginChange="(newValue) => this.filterDateBeginValue = newValue"
              @dateEndChange="(newValue) => this.filterDateEndValue = newValue"
            ></DateRange>
          </v-col>
        </v-row>
        <v-row justify="end" v-if="showDateRangePicker" class="my-0 py-0">
          <v-col cols="12" md="6">
            <FilterButtons
              @search="handleSearchClick"
              @clear="handleClearClick"
            ></FilterButtons>
          </v-col>
        </v-row>
        <FilterButtonsSmall
          v-if="showDateRangePicker"
          @search="handleSearchClick"
          @clear="handleClearClick"
        ></FilterButtonsSmall>
        <v-row class="mt-3">
          <v-col cols="12">
            <SimplePageHeader title="Overtime Entries">
              <template v-slot:right-buttons>
                <v-btn
                  v-if="!readonly"
                  block
                  outlined
                  color="primary"
                  @click="showOvertimeEntryAddModal"
                >
                  Add Entries
                  <v-icon right >add</v-icon>
                </v-btn>
              </template>
            </SimplePageHeader>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-layout column>
              <v-data-table
                dense
                light
                :headers="headers"
                :items="overtimeEntriesList"
                :items-per-page=5
              >
                <template v-slot:item.date="{ item }">
                  {{ item.date | formatDate }}
                </template>
                <template v-slot:item.otPayRate="{ item }">
                  {{ item.otPayRate | currency }}
                </template>
                <template v-slot:item.otBillRate="{ item }">
                  {{ item.otBillRate | currency }}
                </template>
                <template v-slot:item.action="{ item }" v-if="!readonly">
                  <v-btn
                    class="mx-1 my-1"
                    color="primary"
                    outlined
                    @click="showOvertimeEntryEditModal(item)"
                  >
                    <v-icon color="primary">edit</v-icon>
                  </v-btn>
                  <v-btn
                    class="mx-1 my-1"
                    color="red"
                    outlined
                    @click="showOvertimeEntryDeleteDialog(item)"
                  >
                    <v-icon color="red">delete</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-layout>
          </v-col>
        </v-row>
      </v-container>
      <v-container fluid slot="buttons" class="pb-0 mb-0">
        <div class="row d-flex justify-end">
          <div class="col-12">
            <v-btn
              block
              depressed
              outlined
              @click="closeModal"
            >
              Close
            </v-btn>
          </div>
        </div>
      </v-container>
    </DynamicModal>
    <OvertimeEntriesAddModal
      :assignmentId="assignmentId"
      :consultantName="consultantName"
      :clientName="clientName"
      :hourlyPayRate="hourlyPayRate"
      :hourlyBillRate="hourlyBillRate"
      @closeModal="closeOvertimeEntryAddModalHandler"
      ref="OvertimeEntryAddModal"
    ></OvertimeEntriesAddModal>
    <OvertimeEntryEditModal
      :assignmentId="assignmentId"
      :consultantName="consultantName"
      :clientName="clientName"
      :hourlyPayRate="hourlyPayRate"
      :hourlyBillRate="hourlyBillRate"
      :id="overtimeEntryIdToEdit"
      :date="dateToEdit"
      :otPayRate="otPayRateToEdit"
      :otBillRate="otBillRateToEdit"
      :hours="otHoursToEdit"
      @closeModal="closeOvertimeEntryEditModalHandler"
      @dateChange="(newValue) => this.dateToEdit = newValue"
      @otPayRateChange="(newValue) => this.otPayRateToEdit = newValue"
      @otBillRateChange="(newValue) => this.otBillRateToEdit = newValue"
      @hoursChange="(newValue) => this.otHoursToEdit = newValue"
      ref="OvertimeEntryEditModal"
    ></OvertimeEntryEditModal>
    <ConfirmationModal
      :title="'Delete Confirmation'"
      :contentText="'Are you sure you want to delete the selected overtime entry?'"
      :toolbarColor="'primary'"
      @cancel-click="closeOvertimeEntryDeleteConfirmationDialog"
      @ok-click="confirmOvertimeEntryDelete"
      :showModal="showOvertimeDeleteConfirmationDialog"
    ></ConfirmationModal>
  </div>
</template>

<script>
import { DynamicModal, ConfirmationModal } from '@/components/dialogs'
import SimplePageHeader from '@/components/pageComponents/SimplePageHeader'
import { nextTick } from 'vue'
import { mapFields } from '@/vueHelpers/vuex'
import { mapActions } from 'vuex'
import { DateRange } from '@/components/shared/datePickers'
import { FilterButtons, FilterButtonsSmall, ButtonCustomIcon } from '@/components/buttons'
import OvertimeEntriesAddModal from '@/components/overtime/OvertimeEntriesAddModal'
import OvertimeEntryEditModal from '@/components/overtime/OvertimeEntryEditModal'
import { FormattedNumberField } from '@/components/shared/textFields'

export default {
  components: {
    SimplePageHeader,
    DynamicModal,
    ButtonCustomIcon,
    DateRange,
    FilterButtons,
    FilterButtonsSmall,
    OvertimeEntriesAddModal,
    OvertimeEntryEditModal,
    ConfirmationModal,
    FormattedNumberField
  },
  props: [
    'assignmentId',
    'consultantName',
    'clientName',
    'hourlyPayRate',
    'hourlyBillRate',
    'showDateRangePicker',
    'filterDateBegin',
    'filterDateEnd',
    'readonly'
  ],
  data () {
    return {
      showModalProp: false,
      showOvertimeDeleteConfirmationDialog: false,
      overtimeEntryIdToEdit: null,
      dateToEdit: null,
      otPayRateToEdit: null,
      otBillRateToEdit: null,
      otHoursToEdit: null,
      overtimeEntryIdToDelete: null,
      tempFilterDateBegin: null,
      tempFilterDateEnd: null
    }
  },
  computed: {
    ...mapFields('overtimeEntries', ['overtimeEntriesList']),
    headers: {
      get () {
        return this.readonly ? [
          { text: 'Date', value: 'date', align: 'center', width: '19%' },
          { text: 'Hours', value: 'hours', align: 'center', width: '15%', sortable: false },
          { text: 'O/T Pay Rate', value: 'otPayRate', align: 'center', width: '18%', sortable: false },
          { text: 'O/T Bill Rate', value: 'otBillRate', align: 'center', width: '18%', sortable: false }
        ]
          : [
            { text: 'Date', value: 'date', align: 'center', width: '19%' },
            { text: 'Hours', value: 'hours', align: 'center', width: '15%', sortable: false },
            { text: 'O/T Pay Rate', value: 'otPayRate', align: 'center', width: '18%', sortable: false },
            { text: 'O/T Bill Rate', value: 'otBillRate', align: 'center', width: '18%', sortable: false },
            { text: 'Actions', value: 'action', align: 'center', sortable: false, width: '30%' }
          ]
      }
    },
    filterDateBeginValue: {
      get () {
        return this.filterDateBegin ? this.filterDateBegin : this.tempFilterDateBegin
      },
      set (newValue) {
        if (this.filterDateBegin) {
          this.$emit('', newValue)
        } else {
          this.tempFilterDateBegin = newValue
        }
      }
    },
    filterDateEndValue: {
      get () {
        return this.filterDateEnd ? this.filterDateEnd : this.tempFilterDateEnd
      },
      set (newValue) {
        if (this.filterDateEnd) {
          this.$emit('', newValue)
        } else {
          this.tempFilterDateEnd = newValue
        }
      }
    }
  },
  methods: {
    ...mapActions('overtimeEntries', ['getOvertimeEntries', 'deleteOvertimeEntry']),
    async handleSearchClick () {
      await this.showModal()
    },
    async handleClearClick () {
      this.filterDateBeginValue = null
      this.filterDateEndValue = null
      await this.showModal()
    },
    async showModal () {
      await nextTick()
      await this.getOvertimeEntries(Object.assign({}, {
        assignmentId: this.assignmentId,
        dateBegin: this.filterDateBeginValue,
        dateEnd: this.filterDateEndValue
      }))
      this.showModalProp = true
    },
    closeModal () {
      this.showModalProp = false
    },
    async showOvertimeEntryAddModal () {
      this.closeModal()
      await nextTick()
      this.$refs.OvertimeEntryAddModal.showModal()
    },
    closeOvertimeEntryAddModalHandler () {
      this.showModal()
    },
    closeOvertimeEntryEditModalHandler () {
      this.showModal()
    },
    showOvertimeEntryEditModal (item) {
      this.overtimeEntryIdToEdit = item.id
      this.dateToEdit = item.date
      this.otPayRateToEdit = item.otPayRate
      this.otBillRateToEdit = item.otBillRate
      this.otHoursToEdit = item.hours
      this.closeModal()
      this.$refs.OvertimeEntryEditModal.showModal()
    },
    showOvertimeEntryDeleteDialog (item) {
      this.overtimeEntryIdToDelete = item.id
      this.showModalProp = false
      this.showOvertimeDeleteConfirmationDialog = true
    },
    async confirmOvertimeEntryDelete () {
      await this.deleteOvertimeEntry(this.overtimeEntryIdToDelete)
      this.showOvertimeDeleteConfirmationDialog = false
      await this.showModal()
    },
    closeOvertimeEntryDeleteConfirmationDialog () {
      this.showOvertimeDeleteConfirmationDialog = false
      this.showModalProp = true
      this.overtimeEntryIdToDelete = null
    }
  }
}
</script>
